import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import type { CitationOption } from '@/services/types';

/**
 * Citation selector.
 *
 * Boilerplate is FLAGGED, never hidden. Removing the most-cited rules would
 * leave a user wondering why they are missing; the flag says why selecting one
 * gives a population close to the whole corpus. Whether that is actually true is
 * decided by the resulting distribution, not by the flag -- see the notes the
 * backend attaches to the answer.
 */
export function CitationPicker({
  options,
  selected,
  matchAll,
  onToggle,
  onMatchAllChange,
}: {
  options: CitationOption[];
  selected: string[];
  matchAll: boolean;
  onToggle: (citation: string) => void;
  onMatchAllChange: (matchAll: boolean) => void;
}) {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [filter, setFilter] = useState('');
  const number = new Intl.NumberFormat(i18n.language);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matching = needle
      ? options.filter((option) => option.citation.toLowerCase().includes(needle))
      : options;
    return matching.slice(0, 60);
  }, [options, filter]);

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-56 flex-1 space-y-1">
          <span className="text-xs font-medium text-foreground">
            {t('anatel:sanctions.filterCitations')}
          </span>
          <input
            type="search"
            value={filter}
            placeholder={t('anatel:sanctions.filterPlaceholder')}
            onChange={(event) => setFilter(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>

        {/* AND vs OR is the caller's decision and changes the population
            materially, so it is an explicit control rather than a hidden
            default. */}
        <fieldset className="space-y-1">
          <legend className="text-xs font-medium text-foreground">
            {t('anatel:sanctions.combine')}
          </legend>
          <div className="flex gap-1.5">
            {[true, false].map((value) => (
              <button
                key={String(value)}
                type="button"
                aria-pressed={matchAll === value}
                onClick={() => onMatchAllChange(value)}
                className={cn(
                  'rounded border px-2 py-1.5 text-xs',
                  matchAll === value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground',
                )}
              >
                {value ? t('anatel:sanctions.matchAll') : t('anatel:sanctions.matchAny')}
              </button>
            ))}
          </div>
        </fieldset>
      </div>

      {/* No tabIndex here, unlike the page-level scroll container: every row is
          a button, so the region is already reachable and scrollable by keyboard
          and axe does not report scrollable-region-focusable. */}
      {/* 32rem, twice the previous max-h-64. An arbitrary value because
          Tailwind's default max-height scale stops at max-h-96 (24rem), and
          `max-h-128` is not a class -- it would compile to nothing and quietly
          remove the cap rather than double it. */}
      <ul className="max-h-[32rem] space-y-1 overflow-y-auto">
        {visible.map((option) => {
          const active = selected.includes(option.citation);
          return (
            <li key={option.citation}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => onToggle(option.citation)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded border px-2 py-1.5 text-left text-xs',
                  active
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-transparent hover:bg-muted/50 text-muted-foreground',
                )}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="truncate">{option.citation}</span>
                  {option.is_boilerplate ? (
                    <span
                      className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-300"
                      title={t('anatel:sanctions.boilerplateHint', {
                        decisions: option.decisions,
                      })}
                    >
                      {t('anatel:sanctions.boilerplate')}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 tabular-nums">
                  {t('anatel:sanctions.fineCount', { n: number.format(option.fines) })}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
